import React, { useState, useMemo } from 'react';
import ReinnoireCard from '../Shared/ReinnoireCard';
import DateField from '../Shared/DateField';
import ProceduraFileSection from '../Shared/ProceduraFileSection';
import { FilterPill } from '../Shared/FilterPill';
import './Tracker.css';

/* ── Taxe EUIPO default ── */
const TAXE_DEFAULT = {
  ind_basic_efiling: 850,
  ind_basic_paper: 1000,
  col_basic_efiling: 1500,
  col_basic_paper: 1800,
  clasa2: 50,
  clasa3plus: 150,
  sme_pct: 75,
};

const TIP_OPTIONS = [
  ['verbala','Verbala'], ['combinata','Combinata (cuvant + imagine)'],
  ['figurativa','Figurativa (logo/imagine)'], ['stilizata','Caractere Stilizate'],
  ['tridimensionala','Tridimensionala (3D)'], ['sonora','Sonora'], ['alt','Alt tip'],
];

/* ── Helpers ── */
const todayMid = () => { const d = new Date(); d.setHours(0,0,0,0); return d; };
const parseISO = (s) => (s ? new Date(s + 'T00:00:00') : null);
const addMonths = (d, m) => { const x = new Date(d); x.setMonth(x.getMonth() + m); return x; };
const fmtRO = (d) => d ? d.toLocaleDateString('ro-RO', { day:'2-digit', month:'2-digit', year:'numeric' }) : '—';
const daysLeft = (d) => d ? Math.ceil((d - todayMid()) / 86400000) : null;
const fmtEur = (n) => {
  const abs = Math.abs(Number(n));
  return (Number(n) < 0 ? '− ' : '') + abs.toLocaleString('ro-RO') + ' €';
};

/* ── emptyRecord export ── */
export function emptyRecord() {
  return {
    denumire: '',
    natura: 'individuala',
    tip: 'verbala',
    color: 'abn',
    efiling: true,
    clase: {},
    smeFund: false,
    depusa: false,
    numar: '',
    dataDepunere: '',
    dataPublicare: '',
    dataInregistrare: '',
    dataExpirare: '',
    certificatPrimit: false,
    transmisClient: false,
    marcaInregistrata: false,
    viennaCode: '',
    observatii: '',
    achitat_taxa_depunere: false,
    reinnoire_anuntat_client: false,
    reinnoire_depusa: false,
    reinnoire_ciclu_expirare: null,
  };
}

/* ── Fee calculation ── */
function calcTaxe(r, T) {
  const nClase = Object.keys(r.clase).length;
  const nEff = Math.max(1, nClase);
  const colectiva = r.natura === 'colectiva';
  const basic = colectiva
    ? (r.efiling ? T.col_basic_efiling : T.col_basic_paper)
    : (r.efiling ? T.ind_basic_efiling : T.ind_basic_paper);
  let clasaFee = 0;
  if (nEff >= 2) clasaFee += T.clasa2;
  if (nEff >= 3) clasaFee += (nEff - 2) * T.clasa3plus;
  const total = basic + clasaFee;
  const rambursare = r.smeFund ? Math.round(total * (T.sme_pct / 100)) : 0;
  return { nClase, nEff, basic, clasaFee, total, rambursare, net: total - rambursare, colectiva };
}

/* ── Status / timeline ── */
function deriveStatus(r) {
  const pub = parseISO(r.dataPublicare);
  const opozitieEnd = pub ? addMonths(pub, 3) : null;
  const opozitieEnded = opozitieEnd ? todayMid() >= opozitieEnd : false;
  const inreg = parseISO(r.dataInregistrare);
  const registered = !!(r.marcaInregistrata || r.dataInregistrare);

  const steps = [
    { key:'depunere',   label:'Depunere',           done: registered || !!r.depusa,      date: parseISO(r.dataDepunere) },
    { key:'examinare',  label:'Examinare',           done: registered || !!pub,          date: null },
    { key:'publicare',  label:'Publicare cerere',    done: registered || !!pub,          date: pub },
    { key:'opozitie',   label:'Opozitie (3 luni)',   done: registered || opozitieEnded,  date: opozitieEnd },
    { key:'inreg',      label:'Inregistrare',        done: registered || !!inreg,        date: inreg },
    { key:'certificat', label:'Certificat',          done: r.certificatPrimit, date: null },
    { key:'client',     label:'Transmis client',     done: r.transmisClient,   date: null },
  ];

  let alert;
  if (registered) {
    alert = (r.certificatPrimit && !r.transmisClient)
      ? { label:'De transmis certificatul catre client', sev:'act' }
      : { label:'Marca inregistrata', sev:'done' };
  } else if (!r.depusa) {
    alert = { label:'In pregatire — de depus', sev:'neutral' };
  } else if (!pub) {
    alert = { label:'Examinare / asteptare publicare', sev:'info' };
  } else if (!opozitieEnded) {
    alert = { label:'Perioada de opozitie (3 luni)', date: opozitieEnd, sev:'watch' };
  } else if (!inreg) {
    alert = { label:'Opozitie expirata — de confirmat inregistrarea', sev:'act' };
  } else if (!r.certificatPrimit) {
    alert = { label:'Certificat de inregistrare disponibil (electronic)', sev:'act' };
  } else if (!r.transmisClient) {
    alert = { label:'De transmis certificatul catre client', sev:'act' };
  } else {
    alert = { label:'Procedura finalizata', sev:'done' };
  }

  if (alert.date) {
    const dl = daysLeft(alert.date);
    alert.daysLeft = dl;
    if (dl < 0) alert.sev = 'overdue';
  }

  return { steps, alert, opozitieEnd, opozitieEnded };
}

/* ════════════════════════ Component ════════════════════════ */
export default function MarcaEUIPOTracker({ data, onChange, procId, showToast, pendingFiles, onPendingFilesChange }) {
  const [r, setR] = useState(data);
  const [taxe, setTaxe] = useState(TAXE_DEFAULT);
  const [showTaxe, setShowTaxe] = useState(false);

  const set = (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    onChange(next);
  };

  const t = useMemo(() => calcTaxe(r, taxe), [r, taxe]);
  const status = useMemo(() => deriveStatus(r), [r]);

  const toggleClasa = (nr) => {
    const c = { ...r.clase };
    if (c[nr] !== undefined) delete c[nr]; else c[nr] = '';
    set({ clase: c });
  };
  const setTermeni = (nr, val) => set({ clase: { ...r.clase, [nr]: val } });

  return (
    <div className="tracker tracker-euipo">
      <AlertBanner status={status} />
      <Timeline steps={status.steps} />

      {/* Identificare */}
      <TrackerCard title="Identificarea marcii">
        <div className="field">
          <label>Denumire marca</label>
          <input type="text" value={r.denumire} onChange={e => set({ denumire: e.target.value })} placeholder="ex: Aurora Style" />
        </div>
        <div className="f-row">
          <div className="field">
            <label>Natura cererii</label>
            <SegControl value={r.natura} onChange={v => set({ natura: v })} options={[['individuala','Individuala'],['colectiva','Colectiva / certificare']]} />
          </div>
          <div className="field">
            <label>Tip marca</label>
            <FilterPill value={r.tip} onChange={e => set({ tip: e.target.value })}>
              {TIP_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </FilterPill>
          </div>
        </div>
        {r.tip !== 'verbala' && (
          <div className="field">
            <label>Fisiere de reprezentare <span className="f-hint">— logo, imagine, audio, fisier 3D etc.</span></label>
            <ProceduraFileSection procId={procId} showToast={showToast} pendingFiles={pendingFiles} onPendingFilesChange={onPendingFilesChange} />
          </div>
        )}
        {r.tip !== 'verbala' && (
          <div className="field">
            <label>Coduri Vienna <span className="f-hint">— separate prin virgula, ex: 05.03.13, 26.03.01</span></label>
            <input type="text" value={r.viennaCode || ''} onChange={e => set({ viennaCode: e.target.value })} className="mono" placeholder="ex: 05.03.13, 26.04.07" />
          </div>
        )}
        <div className="f-row">
          <div className="field">
            <label>Culoare <span className="f-hint">— nu modifica taxa EUIPO</span></label>
            <SegControl value={r.color} onChange={v => set({ color: v })} options={[['abn','Alb-negru'],['color','Color']]} />
          </div>
          <div className="field">
            <label>Mod de depunere <span className="f-hint">— hartie = taxa mai mare</span></label>
            <SegControl value={r.efiling ? 'e' : 'p'} onChange={v => set({ efiling: v === 'e' })} options={[['e','Electronic'],['p','Hartie / curier']]} />
          </div>
        </div>
      </TrackerCard>

      {/* Clase Nisa */}
      <TrackerCard title={`Clase Nisa (${Object.keys(r.clase).length} selectate)`} collapsible defaultOpen={false}>
        <p className="f-hint mb8">Bifeaza clasele si lipeste produsele/serviciile per clasa. Recomandat: selectare din TMclass pentru Fast Track.</p>
        <div className="clase-grid">
          {CLASE_NISA_LIST.map(([nr, titlu]) => {
            const sel = r.clase[nr] !== undefined;
            return (
              <div key={nr} className={`clasa-item${sel ? ' clasa-sel euipo-sel' : ''}`}>
                <label className="clasa-label">
                  <input type="checkbox" checked={sel} onChange={() => toggleClasa(nr)} />
                  <span className="clasa-nr">{String(nr).padStart(2,'0')}</span>
                  <span className="clasa-titlu">{titlu}</span>
                </label>
                {sel && (
                  <textarea
                    value={r.clase[nr]}
                    onChange={e => setTermeni(nr, e.target.value)}
                    placeholder={`Produse/servicii pt. clasa ${nr}…`}
                    rows={2}
                    className="clasa-textarea"
                  />
                )}
              </div>
            );
          })}
        </div>
      </TrackerCard>

      {/* Calcul taxe */}
      <TrackerCard title="Calcul taxe EUIPO" badge="o taxa per clasa">
        <table className="fee-table">
          <tbody>
            <FeeRow label={`Taxa de baza — 1 clasa (${t.colectiva ? 'colectiva' : 'individuala'}, ${r.efiling ? 'electronic' : 'hartie'})`} value={t.basic} />
            {t.nEff >= 2 && <FeeRow label={`Clasa a 2-a (+${taxe.clasa2} €)`} value={taxe.clasa2} />}
            {t.nEff >= 3 && <FeeRow label={`Clase de la a 3-a (${t.nEff - 2} × ${taxe.clasa3plus} €)`} value={(t.nEff - 2) * taxe.clasa3plus} />}
          </tbody>
          <tfoot>
            <tr className="fee-total">
              <td>Total taxe EUIPO</td>
              <td>{fmtEur(t.total)}</td>
            </tr>
            {r.smeFund && (
              <>
                <tr className="fee-muted">
                  <td>Rambursare SME Fund ({taxe.sme_pct}%)</td>
                  <td className="fee-val">{fmtEur(-t.rambursare)}</td>
                </tr>
                <tr className="fee-total fee-net">
                  <td>Cost net estimat</td>
                  <td>{fmtEur(t.net)}</td>
                </tr>
              </>
            )}
          </tfoot>
        </table>
        {t.nClase === 0 && <p className="f-hint mt4">* Selecteaza cel putin o clasa. Calculul afiseaza minimul pentru 1 clasa.</p>}

        <label className="check-label mt8">
          <input type="checkbox" checked={r.smeFund} onChange={e => set({ smeFund: e.target.checked })} />
          Aplic SME Fund / Voucher (rambursare {taxe.sme_pct}% din taxe)
        </label>

        <button type="button" className="btn-link mt8" onClick={() => setShowTaxe(s => !s)}>
          {showTaxe ? '▾' : '▸'} Taxe oficiale (editabile)
        </button>
        {showTaxe && <TaxeEditor taxe={taxe} setTaxe={setTaxe} />}
      </TrackerCard>

      {/* Depunere + monitorizare */}
      <TrackerCard title="Depunere si monitorizare">
        <label className="check-label">
          <input type="checkbox" checked={r.depusa} onChange={e => set({ depusa: e.target.checked })} />
          <strong>Marca a fost depusa</strong>
        </label>
        <p className="f-hint mb8" style={{ marginLeft: 22 }}>La bifare si introducerea numarului EUTM, porneste urmarirea publicarii si a termenelor.</p>

        {r.depusa && (
          <div className="dep-section">
            <label className="check-label mb8">
              <input type="checkbox" checked={!!r.achitat_taxa_depunere} onChange={e => set({ achitat_taxa_depunere: e.target.checked })} />
              <strong>Marca a fost achitata</strong>
              <span className="f-hint" style={{ marginLeft: 6 }}>— termen 30 zile de la data depunerii</span>
            </label>
            <div className="f-row">
              <div className="field">
                <label>Nr. cerere / inregistrare EUTM</label>
                <input type="text" value={r.numar} onChange={e => set({ numar: e.target.value })} placeholder="ex. 018xxxxxx" className="mono" />
              </div>
              <div className="field">
                <label>Data depunerii</label>
                <DateField value={r.dataDepunere} onChange={v => set({ dataDepunere: v })} />
              </div>
            </div>

            <div className="tr-divider">Publicare &amp; opozitie</div>

            <div className="field">
              <label>Data publicarii cererii</label>
              <DateField value={r.dataPublicare} onChange={v => set({ dataPublicare: v })} />
              <span className="f-hint">Porneste termenul de opozitie de 3 luni.</span>
            </div>

            {status.opozitieEnd && (
              <DeadlineChip label="Sfarsit opozitie (3 luni)" date={status.opozitieEnd} act />
            )}

            <div className="tr-divider">Inregistrare &amp; finalizare</div>

            <div className="field">
              <label>Data inregistrarii</label>
              <DateField value={r.dataInregistrare} onChange={v => set({ dataInregistrare: v })} />
              <span className="f-hint">Dupa expirarea opozitiei, daca nu exista opozitie admisa.</span>
            </div>
            <div className="field">
              <label>Data expirarii</label>
              <DateField value={r.dataExpirare || ''} onChange={v => set({ dataExpirare: v })} />
            </div>

            <label className="check-label">
              <input type="checkbox" checked={r.certificatPrimit} onChange={e => set({ certificatPrimit: e.target.checked })} />
              Certificatul de inregistrare a fost descarcat / primit
            </label>
            <label className={`check-label${!r.certificatPrimit ? ' disabled' : ''}`}>
              <input type="checkbox" disabled={!r.certificatPrimit} checked={r.transmisClient} onChange={e => set({ transmisClient: e.target.checked })} />
              Certificatul a fost transmis catre client
            </label>
            <label className="check-label">
              <input type="checkbox" checked={r.marcaInregistrata || false} onChange={e => set({ marcaInregistrata: e.target.checked })} />
              Statusul marcii este inregistrata
            </label>
          </div>
        )}
      </TrackerCard>

            {/* ── Reinnoire ── */}
      <ReinnoireCard r={r} set={set} TrackerCard={TrackerCard} />

      {/* Observatii */}
      <TrackerCard title="Observatii interne">
        <textarea className="tr-textarea" value={r.observatii} onChange={e => set({ observatii: e.target.value })} rows={3} placeholder="Note de dosar…" />
      </TrackerCard>

      {/* Documente inregistrare */}
      <TrackerCard title="Documente inregistrare">
        <ProceduraFileSection
          procId={procId}
          showToast={showToast}
          pendingFiles={pendingFiles}
          onPendingFilesChange={onPendingFilesChange}
          tipOptions={['Duplicat Cerere inregistrare', 'Copie Certificat Inregistrare']}
          keyPrefix="inreg"
        />
      </TrackerCard>
    </div>
  );
}

/* ── Sub-components (shared with OSIM) ── */
function AlertBanner({ status }) {
  const { alert } = status;
  return (
    <div className={`tr-alert sev-${alert.sev}`}>
      <strong>{alert.label}</strong>
      {alert.date && (
        <span className="tr-alert-date">
          Termen: {fmtRO(alert.date)}
          {typeof alert.daysLeft === 'number' && (
            alert.daysLeft < 0
              ? ` · depasit cu ${Math.abs(alert.daysLeft)} zile`
              : ` · ${alert.daysLeft} zile ramase`
          )}
        </span>
      )}
    </div>
  );
}

function Timeline({ steps }) {
  const activeIdx = steps.findIndex(s => !s.done);
  return (
    <div className="tr-timeline">
      {steps.map((s, i) => {
        const isActive = i === activeIdx;
        const done = s.done;
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <span className={`tl-line${done || steps[i-1].done ? ' tl-line-done' : ''}`} />}
            <div className="tl-step">
              <span className={`tl-dot${done ? ' done' : isActive ? ' active' : ''}`}>
                {done ? '✓' : i + 1}
              </span>
              <span className={`tl-label${isActive ? ' active' : done ? ' done' : ''}`}>{s.label}</span>
              {s.date && <span className="tl-date">{fmtRO(s.date)}</span>}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TrackerCard({ title, badge, children, collapsible = false, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;
  return (
    <div className="tr-card">
      <div
        className={`tr-card-head${collapsible ? ' tr-card-head-toggle' : ''}`}
        onClick={collapsible ? () => setOpen(o => !o) : undefined}
      >
        <span className="tr-card-title">{title}</span>
        <div className="tr-card-head-right">
          {badge && <span className="tr-badge">{badge}</span>}
          {collapsible && <span className={`tr-card-arrow${isOpen ? ' tr-card-arrow-open' : ''}`}>›</span>}
        </div>
      </div>
      {isOpen && <div className="tr-card-body">{children}</div>}
    </div>
  );
}

function SegControl({ value, onChange, options, disabled }) {
  return (
    <div className={`seg-ctrl${disabled ? ' seg-disabled' : ''}`}>
      {options.map(([v, label]) => (
        <button key={v} type="button" disabled={disabled}
          className={`seg-btn${value === v ? ' seg-active' : ''}`}
          onClick={() => !disabled && onChange(v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function FeeRow({ label, value, muted }) {
  return (
    <tr className={muted ? 'fee-muted' : ''}>
      <td>{label}</td>
      <td className="fee-val">{fmtEur(value)}</td>
    </tr>
  );
}

function DeadlineChip({ label, date, act }) {
  const dl = daysLeft(date);
  const overdue = dl < 0;
  const soon = dl >= 0 && dl <= 14;
  const cls = overdue ? 'chip-overdue' : (act && soon) ? 'chip-act' : 'chip-neutral';
  return (
    <div className={`deadline-chip ${cls}`}>
      <span>{label}</span>
      <span>{fmtRO(date)} · {overdue ? `${Math.abs(dl)}z depasit` : `${dl}z`}</span>
    </div>
  );
}

function TaxeEditor({ taxe, setTaxe }) {
  const fields = [
    ['ind_basic_efiling','Ind. baza (electronic)'], ['ind_basic_paper','Ind. baza (hartie)'],
    ['col_basic_efiling','Col. baza (electronic)'], ['col_basic_paper','Col. baza (hartie)'],
    ['clasa2','Clasa a 2-a (€)'], ['clasa3plus','Clasa a 3-a+ (€)'], ['sme_pct','SME Fund (%)'],
  ];
  return (
    <div className="tarife-editor">
      {fields.map(([k, lbl]) => (
        <label key={k} className="tarife-field">
          {lbl}
          <input type="number" value={taxe[k]}
            onChange={e => setTaxe({ ...taxe, [k]: Number(e.target.value) || 0 })} />
        </label>
      ))}
    </div>
  );
}

/* Nice classes list (same 45 classes) */
const CLASE_NISA_LIST = [
  [1,'Produse chimice pentru industrie si stiinta'],
  [2,'Vopsele, lacuri, coloranti, rasini'],
  [3,'Cosmetice, parfumerie, produse de curatare'],
  [4,'Uleiuri si grasimi industriale, combustibili, lumanari'],
  [5,'Produse farmaceutice, de uz medical si veterinar'],
  [6,'Metale comune, materiale de constructie metalice'],
  [7,'Masini, masini-unelte, motoare (nu pt. vehicule)'],
  [8,'Scule si instrumente de mana actionate manual'],
  [9,'Aparate stiintifice, electrice, software, IT'],
  [10,'Aparate si instrumente medicale, chirurgicale, dentare'],
  [11,'Aparate de iluminat, incalzire, racire, gatit, ventilatie'],
  [12,'Vehicule; aparate de locomotie terestra, aeriana, navala'],
  [13,'Arme de foc, munitie, explozibili, artificii'],
  [14,'Metale pretioase, bijuterii, ceasuri'],
  [15,'Instrumente muzicale'],
  [16,'Hartie, carton, tiparituri, papetarie'],
  [17,'Cauciuc, materiale plastice semiprelucrate, izolatoare'],
  [18,'Piele si imitatii, bagaje, selarie, umbrele'],
  [19,'Materiale de constructie nemetalice'],
  [20,'Mobilier, oglinzi, rame; produse din lemn, pluta'],
  [21,'Ustensile si recipiente de menaj si bucatarie, sticlarie'],
  [22,'Franghii, plase, corturi, fibre textile brute'],
  [23,'Fire de uz textil'],
  [24,'Tesaturi si produse textile, lenjerie de casa'],
  [25,'Imbracaminte, incaltaminte, articole pt. acoperirea capului'],
  [26,'Dantele, broderii, panglici, nasturi, accesorii par'],
  [27,'Covoare, presuri, linoleum, tapet'],
  [28,'Jocuri, jucarii, articole de sport, decoratiuni Craciun'],
  [29,'Carne, peste, lactate, conserve, uleiuri alimentare'],
  [30,'Cafea, ceai, faina, paine, patiserie, condimente'],
  [31,'Produse agricole si horticole brute, animale vii, plante'],
  [32,'Bere, ape minerale, bauturi nealcoolice, sucuri'],
  [33,'Bauturi alcoolice (cu exceptia berii)'],
  [34,'Tutun, articole pentru fumatori, chibrituri'],
  [35,'Publicitate, management afaceri, comert cu amanuntul/online'],
  [36,'Servicii financiare, asigurari, imobiliare'],
  [37,'Constructii, reparatii, servicii de instalatii'],
  [38,'Telecomunicatii'],
  [39,'Transport, ambalare si depozitare, organizare calatorii'],
  [40,'Prelucrarea si tratarea materialelor'],
  [41,'Educatie, instruire, divertisment, activitati sportive/culturale'],
  [42,'Servicii stiintifice si tehnologice, IT, design, software'],
  [43,'Servicii de alimentatie publica, cazare temporara'],
  [44,'Servicii medicale, veterinare, igiena, infrumusetare'],
  [45,'Servicii juridice, de securitate, servicii personale'],
];
